<template>
  <ExpiredInviteLink
    v-if="currentDataType === 'invite_expired'"
    :status="inviteRenewalStatus"
    @request-new-link="requestNewInviteLink"
  />
  <v-container v-else fluid>
    <ForgotPassword
      @sendEmailToUser="sendEmailToUser"
      :type="currentDataType"
      @sendNewPassword="resetNewPassword"
      @goToLogin="goToLogin"
    />
  </v-container>
</template>
<script>
import VueRouter from "vue-router";
import ForgotPassword from "@components/ForgotPassword/ForgotPassword.vue";
import { LD_FLAG_IDEA_MDP_611 } from "@components/constants/launchDarklyFlagKeys";
import ExpiredInviteLink from "../components/ExpiredInviteLink/ExpiredInviteLink.vue";
import {
  requestInviteRenewal,
  resetUserPassword,
  sendNewPasswordData,
  signupWithToken,
} from "@/api";

const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

const collectMessages = (data) => {
  if (typeof data === "string") return [data];
  if (Array.isArray(data)) return data.flatMap(collectMessages);
  if (data && typeof data === "object") return Object.values(data).flatMap(collectMessages);
  return [];
};

const getErrorMessages = (error) => collectMessages(error?.response?.data);

export default {
  name: "ForgotPasswordVue",
  components: {
    ForgotPassword,
    ExpiredInviteLink,
  },
  data() {
    return {
      currentDataType: "forgot_password",
      inviteRenewalStatus: "idle",
    };
  },
  mounted() {
    if (
      (this.$route.path == "/setup/password" || this.$route.path == "/reset/password") &&
      this.$route.query.token
    ) {
      this.currentDataType = "set_password";
    } else {
      this.currentDataType = "forgot_password";
    }
  },
  methods: {
    async sendEmailToUser(email) {
      this.$loader.show();
      try {
        await resetUserPassword({ email: email });
        this.currentDataType = "check_email";
      } catch (e) {
        this.$toast.error(getErrorMessages(e)[0] || GENERIC_ERROR_MESSAGE);
      } finally {
        this.$loader.hide();
      }
    },
    async resetNewPassword(data) {
      try {
        const payload = {
          token: this.$route.query.token,
          password: data.newPass,
          password_confirm: data.confirmPass,
        };
        if (this.$route.path === "/setup/password") {
          await signupWithToken(payload);
        } else {
          await sendNewPasswordData(payload);
        }
        this.currentDataType = "password_reset";
      } catch (e) {
        const messages = getErrorMessages(e);
        if (messages.some((message) => /expired/i.test(message))) {
          this.currentDataType = await this.resolveExpiredType();
        } else {
          this.$toast.error(messages[0] || GENERIC_ERROR_MESSAGE);
        }
      }
    },
    async resolveExpiredType() {
      const isInviteLink = this.$route.path === "/setup/password";
      if (isInviteLink && (await this.$getFlag(LD_FLAG_IDEA_MDP_611, false)) === true) {
        return "invite_expired";
      }
      return "token_expired";
    },
    async requestNewInviteLink() {
      if (this.inviteRenewalStatus === "sending" || this.inviteRenewalStatus === "sent") return;
      this.inviteRenewalStatus = "sending";
      try {
        const response = await requestInviteRenewal({ token: this.$route.query.token });
        this.inviteRenewalStatus = response?.data?.rate_limited === true ? "rate_limited" : "sent";
      } catch (e) {
        this.inviteRenewalStatus = "idle";
        this.$toast.error(GENERIC_ERROR_MESSAGE);
      }
    },
    async goToLogin() {
      // Redirected: global guard sends logged-in users away from /login to the app home.
      // Duplicated: same destination as current route. Both are expected, not errors.
      await this.$router.push({ name: "Login" }).catch((err) => {
        if (
          VueRouter.isNavigationFailure(err, VueRouter.NavigationFailureType.redirected) ||
          VueRouter.isNavigationFailure(err, VueRouter.NavigationFailureType.duplicated)
        ) {
          return;
        }
        throw err;
      });
    },
  },
};
</script>
<style scoped></style>
